import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useScrollRevealOnce } from "@/hooks/useScrollRevealOnce";
import {
  BadgePercent,
  HelpCircle,
  MessageCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { openLiveChat } from "@/lib/chat";

/** Current spotlight tiles — swap copy here when the Profit Press feature rotates. */
const pillars = [
  {
    icon: BadgePercent,
    title: "Zero processing fees",
    body: "No processing fees on customer purchases.",
  },
  {
    icon: Sparkles,
    title: "Setup on your terms",
    body: "DIY self-serve when you want speed (great for ecommerce), or guided help from our team—including in person when that fits.",
  },
  {
    icon: ShieldCheck,
    title: "Inclusive eligibility",
    body: "Instant eligibility check—inclusive of all credit types.",
  },
];

const ACCENT = "#E89B2C";

/** In-store terminal (hero) + laptop checkout + retail floor. */
const collageImages = [
  {
    src: "https://images.unsplash.com/photo-1556742031-c6961e8560b0?auto=format&fit=crop&w=900&q=80",
    alt: "Customer paying with a card at a countertop merchant terminal",
    className:
      "col-span-4 row-span-4 rounded-xl sm:rounded-2xl border border-border/80 ring-1 ring-primary/10",
  },
  {
    src: "https://images.unsplash.com/photo-1758522484646-c8694d1784fa?auto=format&fit=crop&w=600&q=80",
    alt: "Person using a laptop with a credit card for an online payment or checkout",
    className:
      "col-span-2 row-span-2 rounded-lg sm:rounded-xl border border-border/80 ring-1 ring-[#E89B2C]/12",
  },
  {
    src: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=600&q=80",
    alt: "Bright retail shop interior with clothing displays",
    className:
      "col-span-2 row-span-2 rounded-lg sm:rounded-xl border border-border/80 ring-1 ring-[#E89B2C]/12",
  },
];

export default function ProfitProtector() {
  const { ref, revealed } = useScrollRevealOnce();

  return (
    <section
      ref={ref}
      id="profit-protector"
      data-testid="profit-protector-section"
      aria-labelledby="profit-protector-heading"
      className="relative py-7 md:py-9 bg-secondary/35 text-foreground overflow-hidden border-y border-border/40"
    >
      {/* Whisper of warmth — keeps the section airy vs. a heavy dark slab */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden
        style={{
          background: `radial-gradient(ellipse 70% 50% at 85% 0%, ${ACCENT}14, transparent 52%)`,
        }}
      />

      <div className="container-tight relative z-[1]">
        <TooltipProvider delayDuration={200}>
          <div
            className={cn(
              "rounded-xl sm:rounded-2xl border border-border/70 bg-card/75 backdrop-blur-sm shadow-sm overflow-hidden",
              "transition-[opacity,transform] duration-700 ease-out",
              revealed
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3 md:translate-y-4"
            )}
          >
            <div className="h-1 w-full bg-gradient-to-r from-primary/25 via-[#E89B2C]/50 to-primary/25" aria-hidden />

            <div className="grid gap-4 sm:gap-5 lg:gap-6 lg:grid-cols-12 lg:items-center p-4 sm:p-5 md:p-6">
              <div className="lg:col-span-7 flex flex-col gap-3 md:gap-3.5 min-w-0 order-2 lg:order-none">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                      OliveUp
                    </span>
                    <span
                      className="text-[10px] sm:text-xs text-muted-foreground"
                      aria-hidden
                    >
                      ·
                    </span>
                    <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#E89B2C]">
                      The Profit Press
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-muted/50 text-muted-foreground hover:text-[#E89B2C] hover:border-[#E89B2C]/35 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label="What is The Profit Press?"
                        >
                          <HelpCircle className="h-3 w-3" strokeWidth={2.2} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={6}
                        className="max-w-[min(18rem,calc(100vw-2rem))] px-3 py-2 text-left text-[13px] leading-snug"
                      >
                        We named it after an olive press—because we love
                        squeezing every dollar back toward your business. This
                        corner gets fresh drops as we line up new ways to guard
                        your margins.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug max-w-xl">
                    A living bulletin of perks and partnerships—we update what
                    lands here as new ways to protect your profits ripen.
                  </p>
                </div>

                <div>
                  <h2
                    id="profit-protector-heading"
                    className="font-display text-lg sm:text-xl md:text-2xl font-bold tracking-tight text-balance text-foreground"
                  >
                    This edition:{" "}
                    <span className="text-[#E89B2C]">$0 processing</span>
                  </h2>
                  <p className="mt-2 text-xs sm:text-sm leading-snug text-muted-foreground max-w-xl">
                    Through a national partner we trust, you can run card
                    processing with{" "}
                    <span className="font-medium text-foreground">
                      no processing fees
                    </span>
                    . Prefer to plug in yourself? You can. Want our team walking
                    you through it? We&apos;re here for that too. Either way,
                    less leakage on every swipe—more money stays in your jar.
                  </p>
                </div>

                <ul className="grid gap-1.5 sm:grid-cols-3 sm:gap-2 lg:grid-cols-3">
                  {pillars.map(({ icon: Icon, title, body }) => (
                    <li
                      key={title}
                      className="flex gap-2 rounded-lg border border-border/80 border-l-2 border-l-[#E89B2C]/50 bg-muted/25 pl-[calc(0.625rem-2px)] pr-2.5 py-2"
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/15">
                        <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-primary leading-tight">
                          {title}
                        </span>
                        <span className="mt-0.5 block text-[11px] sm:text-xs leading-snug text-muted-foreground">
                          {body}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  <a
                    href="#apply"
                    data-testid="profit-protector-cta"
                    className="inline-flex flex-1 min-w-0 sm:flex-none items-center justify-center rounded-full border border-primary/25 bg-primary/10 px-4 py-2 text-xs sm:text-sm font-semibold text-primary hover:bg-primary/15 hover:border-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Explore No-Fee Processing
                  </a>
                  <button
                    type="button"
                    onClick={openLiveChat}
                    data-testid="profit-protector-chat"
                    className="inline-flex flex-1 min-w-0 sm:flex-none items-center justify-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-xs sm:text-sm font-semibold text-foreground hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                    Ask in chat
                  </button>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col min-h-0 order-1 lg:order-none">
                <div className="relative grid grid-cols-6 grid-rows-4 gap-1.5 sm:gap-2 h-[130px] sm:h-[150px] md:h-[160px] lg:h-[188px] lg:shrink-0">
                  {collageImages.map(({ src, alt, className }) => (
                    <div
                      key={src}
                      className={cn(
                        "relative overflow-hidden bg-muted shadow-[inset_0_0_0_1px_hsl(var(--border))]",
                        className
                      )}
                    >
                      <img
                        src={src}
                        alt={alt}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                      />
                      <div
                        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/10 via-transparent to-transparent"
                        aria-hidden
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] sm:text-xs text-muted-foreground leading-snug line-clamp-2">
                  <span className="font-medium text-[#E89B2C]">DIY or guided</span>
                  —same goal: you keep more of every sale.
                </p>
              </div>
            </div>
          </div>
        </TooltipProvider>
      </div>
    </section>
  );
}
